import React, { useId, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Timeline from '@mui/icons-material/Timeline';
import DirectionsRun from '@mui/icons-material/DirectionsRun';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { measurementFamilies } from './relationships';

export default function FitbitMeasurementGuide({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return <>
    {compact ? <Tooltip title="How do these measurements relate?">
      <IconButton size="small" aria-label="How do these measurements relate?" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <HelpOutline fontSize="small" />
      </IconButton>
    </Tooltip> : <Button size="small" startIcon={<HelpOutline />} onClick={() => setOpen(true)}
      aria-haspopup="dialog" aria-expanded={open} sx={{ alignSelf: 'flex-start', px: 0, textTransform: 'none' }}>
      How do these measurements relate?
    </Button>}
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
      aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle id={`${id}-title`}>How measurements relate</DialogTitle>
      <DialogContent>
        <DialogContentText id={`${id}-description`} sx={{ mb: 2 }}>
          Some choices contain individual readings; others summarize days or sessions, or describe parts of a total. Check their scope and units to choose the view that answers your research question.
        </DialogContentText>
        <Stack gap={1.5}>
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Stack direction="row" gap={1} alignItems="center"><Timeline color="action" /><Typography fontWeight={650}>Patterns throughout the day</Typography></Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>Choose Heart rate, Steps, Distance or another individual or daily measurement. These can include activity during and outside workouts, wherever records are available.</Typography>
          </Box>
          <Box sx={{ p: 2, border: '1px solid', borderColor: '#b9d6d8', bgcolor: '#f3f9f9', borderRadius: 2 }}>
            <Stack direction="row" gap={1} alignItems="center"><DirectionsRun sx={{ color: '#256a70' }} /><Typography fontWeight={650}>Recorded exercise sessions</Typography></Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>Choose Workouts for session summaries, including active workout time, calories and average heart rate. Each record describes a workout.</Typography>
          </Box>
        </Stack>
        <Typography variant="body2" sx={{ mt: 2 }}>For example, a walk may contribute to both Steps and Workouts → Steps. Compare the views separately; adding them together can count the same activity twice.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Availability can differ between the two views. Missing individual readings remain missing even when a workout summary exists.</Typography>
        <Accordion disableGutters elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`${id}-families`} id={`${id}-families-title`}>
            <Typography fontWeight={650}>Explore related measurement groups</Typography>
          </AccordionSummary>
          <AccordionDetails id={`${id}-families`}><Stack gap={2}>
            {measurementFamilies.map(family => <Box key={family.title}><Typography variant="body2" fontWeight={650}>{family.title}</Typography>
              <Typography variant="body2" color="text.secondary">{family.description}</Typography></Box>)}
          </Stack></AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button autoFocus onClick={() => setOpen(false)}>Got it</Button></DialogActions>
    </Dialog>
  </>;
}
